import { CheckMarkIcon, CopyIcon, FolderIcon } from '@insta-club/components';
import { AnswerItem, FarsiDate, Operator } from '@insta-club/models';
import { AudioPlayer, Avatar } from '@insta-club/ui';
import { cn, copyToClipboard, sanitizeHtml } from '@insta-club/utilities';
import { cva } from 'class-variance-authority';
import Image from 'next/image';

const fTime = (d: FarsiDate) => `${d.h}:${d.m}`;
const nameOf = (op: Operator) => `${op.first_name ?? ''} ${op.last_name ?? ''}`.trim() || op.username;
const isImage = (url: string) => /\.(png|jpe?g|gif|webp|avif)$/i.test(url);
const isAudio = (url: string) => /\.(mp3|webm|wav|ogg|m4a)$/i.test(url);
const isAudioMime = (mime: string) =>
    /^(audio\/mp3|audio\/webm|audio\/wav|audio\/ogg|audio\/m4a|audio\/mp4)$/i.test(mime);

const rowClass = cva('flex w-full', {
    variants: { side: { operator: 'justify-end', user: 'justify-start' } }
});

const innerRowClass = cva('flex items-end gap-2', {
    variants: { dir: { operator: 'flex-row-reverse', user: 'flex-row' } }
});

const bubbleClass = cva('rounded-2xl px-3.5 py-2.5 shadow-sm w-fit max-w-[290px] break-words', {
    variants: {
        tone: {
            operator: 'bg-blue-50 text-gray-600',
            user: 'bg-gray-100 text-gray-600'
        }
    }
});

const timeClass = cva('mt-1 text-[10px]', {
    variants: {
        tone: {
            operator: 'text-gray-500',
            user: 'text-gray-500'
        }
    }
});

function ChatBubble({ item }: { item: AnswerItem }) {
    const isOperator = !!item.operator;
    const side = isOperator ? 'operator' : ('user' as const);
    const cleanHtml = sanitizeHtml(item.description);

    const handleDownload = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (item.attachment) {
            const link = document.createElement('a');
            link.href = item.attachment;
            link.download = item.attachment.split('/').pop() ?? '';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    return (
        <>
            <div className={cn(rowClass({ side }))}>
                <div className={cn(innerRowClass({ dir: side }))}>
                    {isOperator && item.operator ? (
                        <Avatar
                            src={item.operator.image ?? ''}
                            alt={nameOf(item.operator)}
                            size={28}
                            className='shrink-0'
                        />
                    ) : null}

                    <div className={cn(bubbleClass({ tone: side }))}>
                        {item.title ? <div className='mb-1 text-xs font-medium opacity-80'>{item.title}</div> : null}
                        <div
                            className='text-sm leading-7 whitespace-pre-wrap [&_a]:text-blue-600 [&_a]:underline'
                            dangerouslySetInnerHTML={{ __html: cleanHtml }}
                        ></div>
                        {item.attachment ? (
                            isImage(item.attachment) ? (
                                <a href={item.attachment} target='_blank' rel='noreferrer' className='mt-2 block'>
                                    <Image
                                        height={256}
                                        width={256}
                                        src={item.attachment}
                                        alt='Attachment'
                                        className='h-auto w-full max-w-full rounded-xl border border-black/5'
                                    />
                                </a>
                            ) : isAudio(item.attachment) || isAudioMime(item.mime_type || '') ? (
                                <AudioPlayer fileUrl={item.attachment} audioID={item.id.toString()} />
                            ) : (
                                <div onClick={handleDownload} className='flex cursor-pointer items-center gap-1'>
                                    <span
                                        dir='ltr'
                                        className='relative top-[1px] line-clamp-1 !font-sans text-xs underline'
                                    >
                                        {item.attachment.split('/').pop()}
                                    </span>
                                    <FolderIcon width={38} height={38} />
                                </div>
                            )
                        ) : null}

                        <div className={cn(timeClass({ tone: side }), 'flex items-center space-x-1')}>
                            <CheckMarkIcon width={10} />
                            <span className='h-[10px]'>{item.created_at ? fTime(item.created_at) : null}</span>
                        </div>
                    </div>
                    <div
                        onClick={() => copyToClipboard(item.description)}
                        className='mt-2 flex cursor-pointer items-center justify-center gap-1 self-end rounded-full bg-gray-100 p-2 text-gray-600'
                    >
                        <CopyIcon width={14} height={14} />
                    </div>
                </div>
            </div>
        </>
    );
}

export default ChatBubble;

